import { NonProductiveCategory, NonProductiveCategoryCreate, NonProductiveCategoryUpdate } from '../types/non-productive-category';
import { fetcher } from '../utils/fetcher';

const API_URL = '/api/v1/non-productive-categories';

/**
 * Get all non-productive time categories (system and custom)
 * System categories: Leave, Meetings, Admin, Training (is_billable=false, cannot be deleted)
 */
export const getNonProductiveCategories = async (): Promise<NonProductiveCategory[]> => {
    return await fetcher(`${API_URL}/read`);
};

/**
 * Get a specific non-productive category by ID
 * @param categoryId - The category ID
 */
export const getNonProductiveCategoryById = async (categoryId: string): Promise<NonProductiveCategory> => {
    return await fetcher(`${API_URL}/read/${categoryId}`);
};

/**
 * Create a new non-productive category (user-defined)
 * @param categoryData - The category data to create
 */
export const createNonProductiveCategory = async (categoryData: NonProductiveCategoryCreate): Promise<NonProductiveCategory> => {
    return await fetcher(`${API_URL}/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryData),
    });
};

/**
 * Update a non-productive category (only user-defined categories)
 * @param categoryId - The category ID
 * @param categoryData - The category data to update
 */
export const updateNonProductiveCategory = async (categoryId: string, categoryData: NonProductiveCategoryUpdate): Promise<NonProductiveCategory> => {
    return await fetcher(`${API_URL}/update/${categoryId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryData),
    });
};

/**
 * Delete a non-productive category (only user-defined categories)
 * Note: System categories cannot be deleted (is_system=true)
 * Note: Categories with associated time entries cannot be deleted (DB constraint)
 * @param categoryId - The category ID
 */
export const deleteNonProductiveCategory = async (categoryId: string): Promise<boolean> => {
    return await fetcher(`${API_URL}/delete/${categoryId}`, {
        method: 'DELETE',
    });
};

/**
 * Get system categories only (Leave, Meetings, Admin, Training)
 * Helper function that filters all categories for is_system=true
 */
export const getSystemCategories = async (): Promise<NonProductiveCategory[]> => {
    const allCategories = await getNonProductiveCategories();
    return allCategories.filter(cat => cat.is_system);
};
